import React, { useEffect } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  FileQuestion,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import SitePageLayout from '../components/SitePageLayout';
import { NewsMeta } from '../components/news/NewsListItem';
import NewsPageHero from '../components/news/NewsPageHero';
import { buttonVariants } from '../components/ui/Button';
import { ENVIRONMENT } from '../config/environment';
import { getNewsItem, getNewsItems, getNewsPath } from '../content/news';
import { useMetadata } from '../hooks/useMetadata';
import { useIntl } from 'react-intl';
import styles from './NewsDetail.module.css';

export default function NewsDetail() {
  const intl = useIntl();
  const { contentId } = useParams();
  const { setMetadata } = useMetadata();
  const item = getNewsItem(contentId);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!item) {
      const title = intl.formatMessage({ id: 'news.notFoundTitle' });
      const description = intl.formatMessage({
        id: 'news.notFoundDescription',
      });
      const newsUrl = `${ENVIRONMENT.SITE_URL}/news/`;
      setMetadata({
        lang: 'ja',
        title,
        description,
        ogTitle: title,
        ogDescription: description,
        ogImage: `${ENVIRONMENT.SITE_URL}/ogp-image.jpg`,
        ogUrl: newsUrl,
        canonicalUrl: newsUrl,
        twitterCard: 'summary_large_image',
        twitterTitle: title,
        twitterDescription: description,
        robots: 'noindex,nofollow',
        disableHreflang: true,
      });
      return;
    }

    const canonicalUrl = `${ENVIRONMENT.SITE_URL}${getNewsPath(item.id)}`;
    const title = `${item.title} | 株式会社日本トレカセンター`;
    setMetadata({
      lang: 'ja',
      title,
      description: item.description,
      ogTitle: item.title,
      ogDescription: item.description,
      ogImage: item.thumbnail
        ? item.thumbnail.url
        : `${ENVIRONMENT.SITE_URL}/ogp-image.jpg`,
      ogUrl: canonicalUrl,
      canonicalUrl,
      twitterCard: 'summary_large_image',
      twitterTitle: item.title,
      twitterDescription: item.description,
      robots: undefined,
      disableHreflang: true,
    });
  }, [intl, item, setMetadata]);

  if (!item) {
    return (
      <SitePageLayout>
        <NewsPageHero
          eyebrow={intl.formatMessage({ id: 'news.title' })}
          title={intl.formatMessage({ id: 'news.notFoundTitle' })}
          variant="article"
        />
        <section className="py-16 md:py-24">
          <div className="container max-w-3xl text-center">
            <FileQuestion
              className="mx-auto h-10 w-10 text-muted-foreground"
              aria-hidden="true"
            />
            <p className="mt-6 text-muted-foreground">
              {intl.formatMessage({ id: 'news.notFoundDescription' })}
            </p>
            <Link
              to="/news/"
              className={buttonVariants({
                className: 'mt-8 gap-2 px-6 font-body',
              })}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {intl.formatMessage({ id: 'news.backToList' })}
            </Link>
          </div>
        </section>
      </SitePageLayout>
    );
  }

  // 一覧は releaseDate 降順のため、前の記事（時系列で前）は次の要素になる
  const allItems = getNewsItems().filter(newsItem => !newsItem.isExternal);
  const itemIndex = allItems.findIndex(newsItem => newsItem.id === item.id);
  const newerItem = itemIndex > 0 ? allItems[itemIndex - 1] : null;
  const olderItem =
    itemIndex >= 0 && itemIndex < allItems.length - 1
      ? allItems[itemIndex + 1]
      : null;

  const relatedLink = item.relatedUrl?.startsWith('/') ? (
    <Link
      to={item.relatedUrl}
      className="group flex items-center justify-between gap-5 border border-border bg-card px-5 py-5 text-card-foreground transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-card motion-reduce:transform-none motion-reduce:transition-none sm:px-6"
    >
      <span className="min-w-0 font-medium leading-relaxed">
        {item.relatedLinkLabel ||
          intl.formatMessage({ id: 'news.relatedLink' })}
      </span>
      <ArrowRight
        className="h-5 w-5 shrink-0 text-primary transition-transform group-hover:translate-x-1 motion-reduce:transform-none motion-reduce:transition-none"
        aria-hidden="true"
      />
    </Link>
  ) : (
    <a
      href={item.relatedUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center justify-between gap-5 border border-border bg-card px-5 py-5 text-card-foreground transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-card motion-reduce:transform-none motion-reduce:transition-none sm:px-6"
    >
      <span className="min-w-0 font-medium leading-relaxed">
        {item.relatedLinkLabel ||
          intl.formatMessage({ id: 'news.relatedLink' })}
        <span className="sr-only">
          （{intl.formatMessage({ id: 'news.opensInNewTab' })}）
        </span>
      </span>
      <ArrowUpRight
        className="h-5 w-5 shrink-0 text-primary transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 motion-reduce:transform-none motion-reduce:transition-none"
        aria-hidden="true"
      />
    </a>
  );

  return (
    <SitePageLayout>
      <article lang="ja">
        <NewsPageHero
          eyebrow={intl.formatMessage({ id: 'news.title' })}
          title={item.title}
          variant="article"
        >
          <NewsMeta item={item} linkCategory className="mt-6" />
        </NewsPageHero>

        <div className="container py-12 md:py-16">
          {item.thumbnail && (
            <figure className="mx-auto max-w-3xl overflow-hidden rounded-lg bg-muted">
              <img
                src={item.thumbnail.url}
                width={item.thumbnail.width}
                height={item.thumbnail.height}
                alt=""
                className="h-auto w-full"
              />
            </figure>
          )}

          <div
            className={`${styles.body} mx-auto max-w-3xl ${
              item.thumbnail ? 'mt-10 sm:mt-14' : ''
            }`}
            dangerouslySetInnerHTML={{ __html: item.body }}
          />

          {item.relatedUrl && (
            <aside className="mx-auto mt-14 max-w-3xl border-t border-border pt-8 sm:mt-16">
              <p className="mb-4 font-heading text-xs font-semibold uppercase tracking-eyebrow text-muted-foreground">
                {intl.formatMessage({ id: 'news.relatedLinkTitle' })}
              </p>
              {relatedLink}
            </aside>
          )}

          <nav
            aria-label={intl.formatMessage({ id: 'news.articleNav' })}
            className="mx-auto mt-14 max-w-3xl border-t border-border sm:mt-16"
          >
            {(olderItem || newerItem) && (
              <div className="grid border-b border-border sm:grid-cols-2">
                {olderItem ? (
                  <Link
                    to={getNewsPath(olderItem.id)}
                    className="group flex flex-col gap-2 py-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:pr-6"
                  >
                    <span className="inline-flex items-center gap-1.5 font-heading text-xs font-semibold uppercase tracking-eyebrow text-muted-foreground">
                      <ArrowLeft
                        className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5 motion-reduce:transform-none motion-reduce:transition-none"
                        aria-hidden="true"
                      />
                      {intl.formatMessage({ id: 'news.prevArticle' })}
                    </span>
                    <span className="line-clamp-2 text-sm leading-relaxed text-foreground transition-colors [overflow-wrap:anywhere] group-hover:text-primary">
                      {olderItem.title}
                    </span>
                  </Link>
                ) : (
                  <div aria-hidden="true" className="hidden sm:block" />
                )}
                {newerItem && (
                  <Link
                    to={getNewsPath(newerItem.id)}
                    className={`group flex flex-col gap-2 py-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:items-end sm:pl-6 sm:text-right ${
                      olderItem
                        ? 'border-t border-border sm:border-l sm:border-t-0'
                        : ''
                    }`}
                  >
                    <span className="inline-flex items-center gap-1.5 font-heading text-xs font-semibold uppercase tracking-eyebrow text-muted-foreground">
                      {intl.formatMessage({ id: 'news.nextArticle' })}
                      <ArrowRight
                        className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 motion-reduce:transform-none motion-reduce:transition-none"
                        aria-hidden="true"
                      />
                    </span>
                    <span className="line-clamp-2 text-sm leading-relaxed text-foreground transition-colors [overflow-wrap:anywhere] group-hover:text-primary">
                      {newerItem.title}
                    </span>
                  </Link>
                )}
              </div>
            )}
            <div className="pt-8 text-center">
              <Link
                to="/news/"
                className="inline-flex items-center gap-2 text-sm font-semibold text-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                {intl.formatMessage({ id: 'news.backToList' })}
              </Link>
            </div>
          </nav>
        </div>
      </article>
    </SitePageLayout>
  );
}
